import { kaiPartialIdPresenter } from '@kaiizen/kai-presenters';
import { ApiFeatureFlagRecord, ApiFeatureFlagScope, ApiOrg, ApiUser } from '@kaiizen/kai-pro-api';

/** The org/user picklists use "" to mean "not narrowed to one". */
export const ALL_ORGS = '';
export const ALL_USERS = '';

/** Never renders a bare id when any human-readable handle is available. */
export const getUserDisplayName = (user: ApiUser | undefined): string | undefined =>
  user?.name?.trim() || user?.email?.trim() || user?.phoneNumber?.trim() || undefined;

export const getOrgDisplayName = (org: ApiOrg | undefined): string | undefined => org?.name?.trim() || undefined;

/**
 * Picklist label: the name plus a contact detail, because names repeat and the
 * super admin has to be able to tell two Dana Reeds apart before flipping a flag.
 */
export const getUserSelectorLabel = (user: ApiUser): string => {
  const name = user.name?.trim();
  const contact = user.email?.trim() || user.phoneNumber?.trim() || user.id;
  return name ? `${name} (${contact})` : contact;
};

/**
 * A narrower selection means a narrower write: naming one user targets that
 * user, naming only an org targets the whole org, naming neither goes global.
 */
export const resolveSelectionScope = (
  selectedOrgId: string,
  selectedUserId: string,
): ApiFeatureFlagScope => {
  if (selectedUserId !== ALL_USERS) return 'user';
  if (selectedOrgId !== ALL_ORGS) return 'org';
  return 'global';
};

/** Plain-language description of who a write lands on, for confirmation copy. */
export const describeSelectionTarget = (
  scope: ApiFeatureFlagScope,
  orgName: string | undefined,
  userName: string | undefined,
): string => {
  switch (scope) {
    case 'user':
      return `${userName ?? 'this user'} (all orgs)`;
    case 'org':
      return `everyone in ${orgName ?? 'this org'}`;
    default:
      return 'ALL orgs and ALL users';
  }
};

/**
 * The flag table's heading, which doubles as the statement of what a toggle in it
 * will hit. Naming the scope here means it is never also stated in a separate row.
 */
export const describeFlagsPanelTitle = (
  scope: ApiFeatureFlagScope,
  orgName: string | undefined,
  userName: string | undefined,
  isCurrentUser = false,
): string => {
  switch (scope) {
    // Naming the scope word, not just the target, so "Kaiizen" cannot be
    // mistaken for a person or "Shane" for an org. Calling out "you" matters
    // because editing your own flag is the one case that needs no confirmation.
    case 'user': {
      const reach = isCurrentUser ? '(you; all orgs)' : '(all orgs)';
      return userName
        ? `Feature Flags for user ${userName} ${reach}`
        : `Feature Flags for the selected user ${reach}`;
    }
    case 'org':
      return orgName
        ? `Feature Flags for org ${orgName}`
        : 'Feature Flags for the selected org';
    default:
      return 'Global Feature Flags';
  }
};

/** Identifies an existing record's target, always naming the org or person. */
export const getScopeTargetLabel = (
  flag: ApiFeatureFlagRecord,
  orgsById: Record<string, ApiOrg>,
  usersById: Record<string, ApiUser>,
): string => {
  switch (flag.scope) {
    case 'global':
      return 'All orgs and users';
    case 'org': {
      if (!flag.orgId) return '-';
      const idPart = `org ID: ${kaiPartialIdPresenter(flag.orgId)}`;
      const orgName = getOrgDisplayName(orgsById[flag.orgId]);
      return orgName ? `${orgName} (${idPart})` : `(${idPart})`;
    }
    case 'user': {
      if (!flag.userId) return '-';
      const idPart = `user ID: ${kaiPartialIdPresenter(flag.userId)}`;
      const userName = getUserDisplayName(usersById[flag.userId]);
      return userName ? `${userName} (${idPart})` : `(${idPart})`;
    }
    default:
      return '-';
  }
};

/**
 * Overrides a write will leave untouched, because flag resolution lets the
 * narrowest scope win. Counting them tells the super admin how much of the
 * population this write will *not* reach.
 *
 * `undefined` means "not applicable at this scope"; a number means a real count,
 * including zero.
 */
export interface UnchangedOverrideCounts {
  orgOverrides?: number;
  userOverrides?: number;
}

/**
 * Org membership is only knowable for orgs whose user list has been fetched —
 * `ApiUser` carries no org id. Pass `undefined` for `orgUserIds` when membership
 * is unknown, and the user count is reported as unknown rather than as a wrong
 * number.
 */
export const countUnchangedOverrides = (
  flagRecords: ApiFeatureFlagRecord[],
  flagName: string,
  scope: ApiFeatureFlagScope,
  orgUserIds?: readonly string[],
): UnchangedOverrideCounts => {
  const forFlag = flagRecords.filter(flag => flag.flagName === flagName);

  switch (scope) {
    // Nothing is narrower than a single user, so nothing survives this write.
    case 'user':
      return {};
    case 'org': {
      if (!orgUserIds) return {};
      const inOrg = new Set(orgUserIds);
      return {
        userOverrides: forFlag.filter(flag => flag.scope === 'user' && !!flag.userId && inOrg.has(flag.userId)).length,
      };
    }
    default:
      return {
        orgOverrides: forFlag.filter(flag => flag.scope === 'org').length,
        userOverrides: forFlag.filter(flag => flag.scope === 'user').length,
      };
  }
};

const pluralizeOverrides = (count: number, kind: string) => `${count} ${kind} override${count === 1 ? '' : 's'}`;

/** The trailing note for a confirmation, or undefined when there is nothing to report. */
export const describeUnchangedOverrides = (
  counts: UnchangedOverrideCounts,
): string | undefined => {
  const parts = [
    counts.orgOverrides ? pluralizeOverrides(counts.orgOverrides, 'org') : undefined,
    counts.userOverrides ? pluralizeOverrides(counts.userOverrides, 'user') : undefined,
  ].filter((part): part is string => !!part);

  if (parts.length === 0) return undefined;

  return `Note: ${parts.join(' and ')} for this flag will not change — a narrower scope always wins.`;
};

/** Whether there is anything worth navigating to inspect. */
export const hasUnchangedOverrides = (counts: UnchangedOverrideCounts): boolean => !!counts.orgOverrides || !!counts.userOverrides;

export interface ScopeConfirmation {
  title: string;
  message: string;
  confirmLabel: string;
}

/**
 * The confirmation a write requires, or undefined when it needs none.
 *
 * Only single-user writes go through unchallenged — those affect just the person
 * being targeted. Anything wider has to state the flag, the direction, and who
 * it lands on, so an org-wide or global write can never read as a personal one.
 */
export const buildScopeConfirmation = (
  flagName: string,
  enabled: boolean,
  scope: ApiFeatureFlagScope,
  targetDescription: string,
  unchangedOverrides: UnchangedOverrideCounts = {},
): ScopeConfirmation | undefined => {
  if (scope === 'user') return undefined;

  const direction = enabled ? 'Enable' : 'Disable';
  const isGlobal = scope === 'global';
  const overridesNote = describeUnchangedOverrides(unchangedOverrides);

  return {
    title: isGlobal
      ? `${direction} for ALL orgs and ALL users?`
      : `${direction} for an entire org?`,
    message: [
      `${direction} the feature flag "${flagName}" for ${targetDescription}. This affects everyone in that scope, not just you.`,
      overridesNote,
    ].filter(Boolean).join(' '),
    confirmLabel: `${direction} ${isGlobal ? 'globally' : 'org-wide'}`,
  };
};

/**
 * The "Obsolete" bucket in the flag picker. Double-underscore bracketing keeps it
 * out of the camelCase namespace every real flag name lives in, so it cannot be
 * mistaken for one.
 */
export const OBSOLETE_FLAG_SELECTION = '__obsolete__';

/**
 * Records whose flag name is no longer in the registry. Nothing reads these
 * anymore, so the only useful thing left to do with them is delete them.
 */
export const isObsoleteFlagRecord = (
  flag: ApiFeatureFlagRecord,
  activeFlagNames: readonly string[],
): boolean => !activeFlagNames.includes(flag.flagName);

export const getObsoleteFlagRecords = (
  flagRecords: ApiFeatureFlagRecord[],
  activeFlagNames: readonly string[],
): ApiFeatureFlagRecord[] => {
  const active = new Set(activeFlagNames);
  return flagRecords.filter(flag => !active.has(flag.flagName));
};

/**
 * The confirmation a delete requires, or undefined when it needs none.
 *
 * Deleting an obsolete record changes nothing observable — no code reads that
 * flag — so cleanup stays one click. Deleting a live record hands resolution
 * back to a wider scope, which can flip the effective value; "may" rather than
 * "will" because the outcome depends on what the wider scopes say.
 */
export const buildDeleteConfirmation = (
  flag: ApiFeatureFlagRecord,
  scopeTargetLabel: string,
  activeFlagNames: readonly string[],
): ScopeConfirmation | undefined => {
  if (isObsoleteFlagRecord(flag, activeFlagNames)) return undefined;

  const direction = flag.enabled ? 'disable' : 'enable';

  return {
    title: 'Delete this feature flag record?',
    message: `Deleting this record may ${direction} "${flag.flagName}" for ${scopeTargetLabel}.`,
    confirmLabel: 'Delete record',
  };
};

/** The records the flag picker should show for the current selection. */
export const getRecordsForFlagSelection = (
  sortedFlagRecords: ApiFeatureFlagRecord[],
  flagSelection: string | undefined,
  activeFlagNames: readonly string[],
): ApiFeatureFlagRecord[] => {
  if (!flagSelection) return [];
  if (flagSelection === OBSOLETE_FLAG_SELECTION) {
    return getObsoleteFlagRecords(sortedFlagRecords, activeFlagNames);
  }
  return sortedFlagRecords.filter(flag => flag.flagName === flagSelection);
};

/** Describes a write for the toast, always in terms of the value being written. */
export const describeFlagWrite = (
  flagName: string,
  enabled: boolean,
  targetDescription: string,
): string => `${enabled ? 'enable' : 'disable'} ${flagName} for ${targetDescription}`;
